import { isEmpty, isNil } from "lodash/fp";
import { useParams } from "react-router-dom";

import Stack from "@mui/material/Stack";

import { TabContent } from "@app/analytics/account/WorkspaceSettings/components/styles";
import ROUTES from "@app/common/routes";
import { EnterpriseTeamSettings } from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";

import LoadSpinner from "@rui/LoadSpinner";

import TeamActionsModule from "./TeamActionsModule";
import TeamReportingFeaturesModule from "./TeamReportingFeaturesModule";
import TeamSharingModule from "./TeamSharingModule";
import SettingAlert from "../../components/SettingAlert";
import Breadcrumbs from "../components/Breadcrumb";
import useWorkspaceTeam from "../hooks/useWorkspaceTeam";

const TeamSettings = () => {
  const { updateTeamSettings, workspace, workspaceId, workspaceIsInactive } =
    useWorkspace();
  const { teamId } = useParams();

  const workspaceTeam = workspace.teams.find((t) => t.id === teamId)!;
  const { error, loading, team } = useWorkspaceTeam(teamId);

  const showRestrictedAccessAlert = Object.values(team.settings).some(
    (setting) => !setting.edit
  );

  const handleTeamSettingUpdate =
    (settingKey: keyof EnterpriseTeamSettings) => (membersCanView: boolean) =>
      updateTeamSettings({
        teamId: teamId!,
        settings: {
          [settingKey]: { view: membersCanView },
        },
      });

  if (isNil(workspaceTeam)) {
    return (
      <Stack sx={{ height: "100%", width: "100%" }}>
        <SettingAlert show persist severity="error">
          Sorry, this team does not exist.
        </SettingAlert>
      </Stack>
    );
  }

  if (isEmpty(team.settings)) {
    return (
      <Stack sx={{ height: "100%", width: "100%" }}>
        <SettingAlert show persist severity="warning">
          Sorry, you do not have access to edit this team.
        </SettingAlert>
      </Stack>
    );
  }

  const breadcrumbItems = [
    {
      label: "Teams",
      link: ROUTES.workspaceSettingsById(workspaceId!),
    },
    {
      label: workspaceTeam.name,
      link: ROUTES.workspaceTeamById(workspaceId!, teamId!),
    },
    {
      label: "Configure Team",
    },
  ];

  return (
    <Stack sx={{ height: "100%", width: "100%" }}>
      <Breadcrumbs items={breadcrumbItems} />
      <Stack direction="column" sx={{ mx: 2, mt: 2 }}>
        {loading ? (
          <LoadSpinner sx={{ minHeight: "100px" }} />
        ) : (
          <TabContent>
            <SettingAlert
              show={showRestrictedAccessAlert}
              persist
              severity="info"
            >
              Some Team settings have been restricted by the Workspace Owner or
              Admin.
            </SettingAlert>
            <SettingAlert show={error} persist severity="error">
              Something went wrong when fetching the Team settings. Please
              refresh the page or come back later.
            </SettingAlert>
            <Stack
              direction="column"
              sx={{
                borderBottom: "1px solid",
                borderColor: "shades.neutral.100",
              }}
            >
              <TeamSharingModule
                onSettingUpdate={handleTeamSettingUpdate}
                settings={team.settings}
                workspaceIsInactive={workspaceIsInactive}
              />
              <TeamReportingFeaturesModule
                onSettingUpdate={handleTeamSettingUpdate}
                settings={team.settings}
                workspaceIsInactive={workspaceIsInactive}
              />
              <TeamActionsModule teamId={teamId!} />
            </Stack>
          </TabContent>
        )}
      </Stack>
    </Stack>
  );
};

export default TeamSettings;
